import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal, Kicker } from "@/components/Reveal";
import { images, LOGO_URL } from "@/data/content";

const About = () => {
  const { t } = useLanguage();
  const a = t.aboutPage;

  return (
    <div data-testid="about-page" className="bg-cream">
      {/* Intro */}
      <section className="pt-40 md:pt-52 pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Kicker>{a.kicker}</Kicker>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-serif font-light text-5xl sm:text-6xl md:text-7xl leading-[1.05] mt-6 text-charcoal max-w-4xl">
              {a.title}
            </h1>
          </Reveal>
        </div>
      </section>

      {/* Image band */}
      <section className="pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="overflow-hidden aspect-[16/9] bg-cream-2">
              <img
                src={images.aboutHero}
                alt="BY VAYA coastal lifestyle"
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Story */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
            <div className="md:col-span-4">
              <Reveal>
                <p className="font-serif text-3xl md:text-4xl text-olive leading-snug">
                  {t.about.experience}
                </p>
              </Reveal>
            </div>
            <div className="md:col-span-7 md:col-start-6 space-y-8">
              {[a.p1, a.p2, a.p3].map((para, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <p className="text-lg text-charcoal/75 font-light leading-relaxed">
                    {para}
                  </p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Founder — Marie */}
      <section className="bg-cream-2 py-24 md:py-32" data-testid="about-founder">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
            <div className="md:col-span-5">
              <Reveal>
                <div className="overflow-hidden aspect-[4/5] bg-sand">
                  <img
                    src={images.founder}
                    alt={a.founder.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </Reveal>
            </div>
            <div className="md:col-span-6 md:col-start-7">
              <Reveal>
                <Kicker>{a.founder.kicker}</Kicker>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="font-serif font-light text-5xl sm:text-6xl text-charcoal mt-5">
                  {a.founder.name}
                </h2>
              </Reveal>
              <Reveal delay={0.14}>
                <p className="font-mono text-[0.7rem] uppercase tracking-[0.22em] text-sage mt-3">
                  {a.founder.role}
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="space-y-6 mt-8">
                  <p className="text-charcoal/75 font-light leading-relaxed">
                    {a.founder.bio1}
                  </p>
                  <p className="text-charcoal/75 font-light leading-relaxed">
                    {a.founder.bio2}
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Brand mark */}
      <section className="bg-cream py-32 md:py-48 border-t border-border-subtle" data-testid="about-logo">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex justify-center">
          <Reveal>
            <img
              src={LOGO_URL}
              alt="BY VAYA"
              className="w-full max-w-[16rem] md:max-w-xs h-auto object-contain"
            />
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="bg-cream-2 py-24 md:py-32" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Kicker>{a.valuesTitle}</Kicker>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border-subtle border border-border-subtle mt-10">
            {a.values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.06}>
                <div className="bg-cream-2 p-8 md:p-10 h-full min-h-[220px] flex flex-col justify-between">
                  <span className="font-mono text-xs text-taupe">0{i + 1}</span>
                  <div>
                    <h3 className="font-serif text-3xl text-charcoal mb-3">
                      {v.title}
                    </h3>
                    <p className="text-charcoal/60 font-light text-sm leading-relaxed">
                      {v.desc}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
